/*global chrome*/

import { EXTENSION_CONFIG } from '../config/extensionConfig'

class ExtensionService {
	constructor() {
		this.isListening = false
		this.messageHandlers = new Map()
		this.pendingRequests = new Map()
		this.requestId = 0

		this.EXTENSION_ID = EXTENSION_CONFIG.PRIMARY_ID
		this.EXTENSION_NAME = EXTENSION_CONFIG.EXTENSION_NAME
		this.STORE_URL = EXTENSION_CONFIG.STORE_URL

		this.setupMessageListener()
	}

	setupMessageListener() {
		if (this.isListening) return

		window.addEventListener('message', event => {
			if (event.origin !== window.location.origin) return

			if (event.data.type === 'FROM_EXTENSION') {
				this.handleExtensionMessage(event.data)
			}
		})

		this.isListening = true
	}

	handleExtensionMessage(data) {
		const { requestId, action, payload, error } = data

		if (requestId && this.pendingRequests.has(requestId)) {
			const { resolve, reject } = this.pendingRequests.get(requestId)
			this.pendingRequests.delete(requestId)

			if (error) {
				reject(new Error(error))
			} else {
				resolve(payload)
			}
			return
		}

		if (this.messageHandlers.has(action)) {
			this.messageHandlers.get(action)(payload)
		}
	}

	sendMessage(action, data = {}, timeout = 5000) {
		return new Promise((resolve, reject) => {
			const requestId = ++this.requestId

			let isResolved = false
			this.pendingRequests.set(requestId, { resolve, reject })

			const timeoutId = setTimeout(() => {
				if (this.pendingRequests.has(requestId) && !isResolved) {
					this.pendingRequests.delete(requestId)
					isResolved = true
					const timeoutError = new Error(
						action === 'postTruck'
							? 'Extension took too long to respond. The extension might need to be updated or reloaded.'
							: 'Extension request timeout'
					)
					timeoutError.isTimeout = true
					console.log(
						`❌ Rejecting with timeout error for request ${requestId}`
					)
					reject(timeoutError)
				}
			}, timeout)

			if (
				typeof chrome !== 'undefined' &&
				chrome.runtime &&
				chrome.runtime.sendMessage
			) {
				try {
					chrome.runtime.sendMessage(
						this.EXTENSION_ID,
						{
							type: 'FROM_WEBSITE',
							action,
							requestId,
							data,
						},
						response => {
							clearTimeout(timeoutId)

							if (isResolved) return

							if (chrome.runtime.lastError) {
								if (this.pendingRequests.has(requestId)) {
									this.pendingRequests.delete(requestId)
									isResolved = true
									reject(
										new Error(
											`Extension communication failed: ${chrome.runtime.lastError.message}`
										)
									)
								}
								return
							}

							if (this.pendingRequests.has(requestId)) {
								this.pendingRequests.delete(requestId)
								isResolved = true

								if (response && response.error) {
									const responseError = new Error(response.error)
									responseError.isLocalMode = Boolean(response.localMode)
									reject(responseError)
								} else {
									resolve(response)
								}
							}
						}
					)
				} catch (error) {
					clearTimeout(timeoutId)
					if (this.pendingRequests.has(requestId) && !isResolved) {
						this.pendingRequests.delete(requestId)
						isResolved = true
						reject(new Error(`Extension API error: ${error.message}`))
					}
				}
			} else {
				window.postMessage(
					{
						type: 'FROM_WEBSITE_TO_EXTENSION',
						extensionId: this.EXTENSION_ID,
						action,
						requestId,
						data,
					},
					window.location.origin
				)
			}
		})
	}

	// Sent with the tracked error so a failed ping can be told apart:
	// no way to message the extension vs. the extension answering too slowly.
	describePingFailure(error, pingDurationMs) {
		return {
			canMessageExtension:
				typeof chrome !== 'undefined' &&
				typeof chrome.runtime?.sendMessage === 'function',
			pingError: error.message,
			pingDurationMs,
			extensionId: this.EXTENSION_ID,
			pageOpenMinutes: Math.round(performance.now() / 60000),
			pageVisibility: document.visibilityState,
			online: navigator.onLine,
			userAgent: navigator.userAgent,
		}
	}

	async checkExtensionCompatibility() {
		const pingStartedAt = Date.now()
		try {
			// Try to ping the extension
			const response = await this.sendMessage(
				'ping',
				{},
				EXTENSION_CONFIG.TIMEOUTS.PING
			)

			// If we get a response, extension is compatible
			return {
				compatible: true,
				installed: true,
				localMode: Boolean(response?.localMode),
			}
		} catch (error) {
			const diagnostics = this.describePingFailure(
				error,
				Date.now() - pingStartedAt
			)

			// Check if it's a timeout error
			if (
				error.isTimeout ||
				error.message?.includes('timeout') ||
				error.message?.includes('request timeout')
			) {
				// Timeout means extension exists but doesn't support communication
				return {
					compatible: false,
					diagnostics,
					installed: true,
					message:
						'Extension is outdated and does not support website communication. Please update to the latest version.',
					action: 'update',
				}
			}

			// Check if it's a "could not establish connection" error
			if (
				error.message?.includes('Could not establish connection') ||
				error.message?.includes('Receiving end does not exist')
			) {
				// Extension not installed
				return {
					compatible: false,
					diagnostics,
					installed: false,
					message: 'Extension is not installed',
					action: 'install',
				}
			}

			// Other errors - assume extension not installed
			return {
				compatible: false,
				diagnostics,
				installed: false,
				message: 'Failed to communicate with extension',
				error: error.message,
				action: 'install',
			}
		}
	}

	async postTruck(loadData) {
		try {
			const compatibility = await this.checkExtensionCompatibility()

			if (!compatibility.compatible) {
				throw new Error(compatibility.message)
			}

			if (compatibility.localMode) {
				const localModeError = new Error(
					'Local Mode is on in the LoadFetcher extension. Turn it off to post trucks.'
				)
				localModeError.isLocalMode = true
				throw localModeError
			}

			const response = await this.sendMessage(
				'postTruck',
				{
					load: loadData,
					timestamp: new Date().toISOString(),
				},
				EXTENSION_CONFIG.TIMEOUTS.POST_TRUCK
			)

			return { success: true, data: response }
		} catch (error) {
			return {
				success: false,
				error: error.message,
				localMode: Boolean(error.isLocalMode),
			}
		}
	}

	onMessage(action, handler) {
		this.messageHandlers.set(action, handler)
	}

	removeMessageHandler(action) {
		this.messageHandlers.delete(action)
	}
}

const extensionService = new ExtensionService()

export default extensionService
